import { useApolloClient } from '@apollo/client';
import {
  Box,
  Divider,
  Flex,
  Heading,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
} from '@chakra-ui/react';
import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { z } from 'zod';

import {
  finleyAssertUnreachable,
  path as commonPath,
} from '@finley-technologies/common';
import { CreditAgreement, Util } from '@finley-technologies/finley-types';

import {
  UploadDocumentFileExtensions,
  UploadSpreadsheetFileExtensions,
} from './UploadTemplateDropzone';
import { UploadTemplateForm } from './UploadTemplateForm';
import {
  DeleteIconButton,
  DownloadIconButton,
  UploadIconButton,
} from 'packages/common-frontend/components/IconButton';
import { LabeledDetail } from 'packages/common-frontend/components/LabeledDetail';
import { errorToastConfig } from 'packages/common-frontend/constants';
import { CreditAgreementFullArtifactWipConfigType } from 'packages/creditAgreements/ui/types';
import { GET_REPORTING_TEMPLATE_UPLOAD_SIGNED_URL } from 'packages/files/requests/files.gql';
import { presignedPostUpload } from 'packages/files/ui/utils';
import { useDownloadReportTemplateUrl } from 'packages/reporting/hooks/useDownloadReportTemplateUrl';

// Types
const RouteParamsStruct = z.object({
  creditFacilityId: z.string({ message: 'Missing URL parameter' }),
});
type TemplateConfigType = CreditAgreement.Config.Reporting.Outputs.Xlsx.Config['template'];

// Helpers
const getAllowedFileExtensions = (
  fileType: CreditAgreement.Config.Reporting.ReportTemplateFileType,
) => {
  switch (fileType) {
    case 'docx':
      return UploadDocumentFileExtensions;
    case 'xlsx':
      return UploadSpreadsheetFileExtensions;
    case 'digital':
      return []; // digital does not support any extensions
    default:
      return finleyAssertUnreachable(fileType);
  }
};

// Exports
type ReportTemplateContainerProps = {
  creditAgreementId: string;
  reportConfigType: CreditAgreement.DomainEntity.ReportConfigType;
  templateConfig: TemplateConfigType;
  reportName: string;
  templatePropertyKey: string;
  fileType: CreditAgreement.Config.Reporting.ReportTemplateFileType;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
};
export const ReportTemplateContainer = ({
  reportConfigType,
  reportName,
  templateConfig,
  templatePropertyKey,
  wipConfig,
  fileType,
  creditAgreementId,
}: ReportTemplateContainerProps) => {
  const toast = useToast();
  const client = useApolloClient();
  const params = useParams();
  const { creditFacilityId } = RouteParamsStruct.parse(params);
  const allowedExtensions = getAllowedFileExtensions(fileType);

  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);
  const onClose = () => {
    setShowUploadModal(false);
  };

  const { getReportTemplateDownload, isLoading } = useDownloadReportTemplateUrl(
    {
      creditFacilityId,
      creditAgreementId,
      reportName,
      templateConfig,
    },
  );

  const uploadFileAndSave = useCallback(
    async (file: File): Promise<void> => {
      if (!wipConfig?.isMutateAllowed(templatePropertyKey)) {
        return;
      }

      try {
        // Upload template to S3: params align with "ReportingTemplateUploadDestinationRequest"
        const { s3FilePath } = await presignedPostUpload<
          {
            creditFacilityId: string;
            fileExtension: Util.OutputReportFileExtension;
            reportConfigType: CreditAgreement.DomainEntity.ReportConfigType;
          },
          {}
        >({
          client,
          file,
          query: GET_REPORTING_TEMPLATE_UPLOAD_SIGNED_URL,
          queryVariables: {
            creditFacilityId,
            fileExtension: commonPath.getOutputReportFileExtension(file.name),
            reportConfigType,
          },
        });

        // Update CA with new template metadata
        const updatedTemplateObject = {
          name: file.name,
          s3Path: s3FilePath,
        };
        wipConfig.saveAndUpdateConfigCreditAgreementWip({
          wipConfig,
          propertyKey: templatePropertyKey,
          updatedProperty: updatedTemplateObject,
        });
      } catch (err: any) {
        toast({
          ...errorToastConfig,
          id: undefined, // Avoid merging timeouts if multiple errors.
          title: 'Error processing upload. Please try again!',
        });
      }
    },
    [
      client,
      creditFacilityId,
      reportConfigType,
      templatePropertyKey,
      toast,
      wipConfig,
    ],
  );

  return (
    <Box id="template-upload-container" mb="4" borderWidth="1px" p="4">
      <Flex mb="5" alignItems="center">
        <Heading fontSize="large" as="h4" mr="3">
          Template
        </Heading>

        {wipConfig?.isMutateAllowed(templatePropertyKey) && (
          <>
            <UploadIconButton
              ariaLabel={`${templateConfig ? 'update' : 'add'} template`}
              label={`${templateConfig ? 'Update' : 'Add'} Template`}
              onClick={() => setShowUploadModal(true)}
            />

            {templateConfig && (
              <>
                <DeleteIconButton
                  ariaLabel="remove template"
                  label="Remove Template"
                  onClick={() => {
                    wipConfig.formProps.setFieldValue(
                      templatePropertyKey,
                      undefined,
                    );
                  }}
                />
                <DownloadIconButton
                  ariaLabel="download template"
                  label="Download Template"
                  fontSize="xl"
                  isLoading={isLoading}
                  onClick={() => {
                    getReportTemplateDownload();
                  }}
                />
              </>
            )}
          </>
        )}
      </Flex>

      {templateConfig ? (
        <Box mb="2">
          <LabeledDetail label="Name" detailFontSize="16px">
            {templateConfig.name}
          </LabeledDetail>

          <Divider my="3" />

          <LabeledDetail label="S3 Path" detailFontSize="16px">
            {templateConfig.s3Path}
          </LabeledDetail>
        </Box>
      ) : (
        <Text mb="2">No template currently configured.</Text>
      )}

      <Modal isOpen={showUploadModal} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent p="5" width="auto" minW="xl">
          <ModalHeader>Upload Report Template</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <UploadTemplateForm
              allowedExtensions={allowedExtensions}
              multiple={false}
              onClose={onClose}
              handleFile={uploadFileAndSave}
            />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};
